import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Play, X, Zap, Settings, Code, BookOpen, HelpCircle, MessageCircle, Map } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMobile } from "../hooks/useMobile";
import { AITutorDualModePanel, type AITutorDualMode } from "../components/AITutorDualModePanel";
import type { TopicSelectPayload } from "../components/assessment/TopicPanel";
import type { TelegramUser } from "../types";

interface AITutorProps {
  user: TelegramUser | null;
}

/** Блок возможностей для модалки «Демо» */
const DEMO_CAPABILITIES = [
  {
    id: "optimization",
    title: "Оптимизация запросов",
    description: "Помогает ускорить запросы: подскажет индексы, порядок условий, использование PREWHERE и других приёмов ClickHouse.",
    icon: Zap,
    examples: [
      "Почему мой запрос медленный? Как его ускорить?",
      "Оптимизируй этот SELECT: …",
      "Когда использовать PREWHERE вместо WHERE?",
    ],
  },
  {
    id: "config",
    title: "Тонкости настройки",
    description: "Объясняет параметры движков, партиционирование, TTL, сжатие и настройки окружения.",
    icon: Settings,
    examples: [
      "Как настроить партиционирование для MergeTree?",
      "Чем отличаются настройки сжатия?",
      "Как включить TTL для колонки?",
    ],
  },
  {
    id: "examples",
    title: "Генерация примеров",
    description: "Генерирует примеры запросов под вашу схему: агрегации, джойны, оконные функции, работа с массивами и т.д.",
    icon: Code,
    examples: [
      "Покажи пример запроса с GROUP BY и агрегатами",
      "Как сделать JOIN двух таблиц в ClickHouse?",
      "Пример работы с массивами и arrayJoin",
    ],
  },
  {
    id: "explain",
    title: "Объяснение сложных тем",
    description: "Разбирает концепции простым языком: колоночное хранение, MergeTree, репликация, словари, материализованные представления.",
    icon: BookOpen,
    examples: [
      "Объясни, как работает MergeTree",
      "Что такое колоночное хранение и зачем оно?",
      "Чем ReplacingMergeTree отличается от обычного MergeTree?",
    ],
  },
];

const DEMO_FAQ = [
  {
    q: "Что умеет AI Tutor?",
    a: "Отвечает на вопросы по ClickHouse, опираясь на материалы курса и документацию: оптимизирует запросы, объясняет настройки, генерирует примеры и разбирает сложные темы.",
  },
  {
    q: "Как задавать вопрос?",
    a: "Напишите вопрос в чате или выберите один из предложенных. В тренажёре и на странице урока контекст (задача, код) подставляется автоматически.",
  },
  {
    q: "Откуда AI берёт ответы?",
    a: "Используется RAG: поиск по материалам курса и релевантным статьям, затем ответ формирует языковая модель. Примеры SQL строятся с учётом реальных таблиц из курса.",
  },
];

const NAV_TABS: { key: AITutorDualMode; label: string; Icon: typeof MessageCircle }[] = [
  { key: "chat", label: "Чат", Icon: MessageCircle },
  { key: "roadmap", label: "Roadmap", Icon: Map },
];

export const AITutor = ({ user }: AITutorProps) => {
  const isMobile = useMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [mode, setMode] = useState<AITutorDualMode>("chat");
  const [initialTopic, setInitialTopic] = useState<TopicSelectPayload | null>(null);

  // Open specific topic from navigation state (e.g. from quiz modal)
  useEffect(() => {
    const state = location.state as { openTopic?: TopicSelectPayload } | null;
    if (state?.openTopic) {
      setMode("roadmap");
      setInitialTopic(state.openTopic);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const headerOffset = isMobile ? "5rem" : "var(--header-height, 4.5rem)";

  return (
    <div
      className={`flex bg-[#fafafa] overflow-hidden ${isMobile ? "fixed inset-0" : ""}`}
      style={{
        height: isMobile ? "100dvh" : "100vh",
        paddingTop: headerOffset,
        boxSizing: "border-box",
      }}
    >
      {/* ── Left sidebar (desktop only) ── */}
      {!isMobile && (
        <aside className="w-56 flex-shrink-0 border-r border-neutral-border flex flex-col bg-white overflow-hidden">
          {/* Title + description */}
          <div className="px-5 pt-6 pb-4">
            <h1 className="text-xl font-heading font-semibold text-neutral-text tracking-tight">
              AI Tutor
            </h1>
            <p className="text-xs text-neutral-text/55 mt-1 leading-relaxed">
              Задавайте вопросы о ClickHouse — AI поможет разобраться
            </p>
          </div>

          {/* Как это работает */}
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={() => setIsDemoModalOpen(true)}
              className="w-full flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
              title="Посмотреть возможности чата"
            >
              <Play size={14} className="flex-shrink-0" />
              Как это работает
            </button>
          </div>

          <div className="border-t border-neutral-border mx-4 mb-3" />

          {/* Mode tabs */}
          <nav className="px-3 space-y-0.5" aria-label="Режим AI Tutor">
            {NAV_TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  mode === key
                    ? "bg-primary/10 text-primary"
                    : "text-neutral-text hover:bg-neutral-light"
                }`}
                aria-pressed={mode === key}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {label}
              </button>
            ))}
          </nav>
        </aside>
      )}

      {/* ── Main content area ── */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
        {/* Mobile: compact top bar with tabs */}
        {isMobile && (
          <div className="flex-shrink-0 flex items-center gap-1 px-3 py-2 border-b border-neutral-border bg-white">
            <span className="text-base font-semibold text-neutral-text flex-1 truncate">AI Tutor</span>
            {NAV_TABS.map(({ key, label, Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  mode === key
                    ? "bg-primary/10 text-primary"
                    : "text-neutral-text/70 hover:bg-neutral-light hover:text-neutral-text"
                }`}
                aria-pressed={mode === key}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Chat + Roadmap via unified panel (topic panel handled internally) */}
        <AITutorDualModePanel
          user={user}
          onClose={() => navigate(-1)}
          omitHeader
          mode={mode}
          onModeChange={setMode}
          showCloseButton
          narrowChatColumn
          chatProps={{
            embedded: true,
            hideHeader: true,
            floatingInput: !isMobile,
            showCloseButton: false,
            contextType: "generic",
            enableFeedback: true,
            showChatHistoryInEmbedded: true,
            initialQuestionsSource: "ai_tutor_page",
            containerClassName: "flex-1 min-h-0 flex flex-col overflow-hidden",
          }}
          roadmapProps={{ isMobile }}
          initialTopic={initialTopic}
          onInitialTopicConsumed={() => setInitialTopic(null)}
        />
      </div>

      {/* Модальное окно: FAQ и примеры возможностей */}
      {isDemoModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-[99999] p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="demo-modal-title"
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
              <div className="flex items-center justify-between flex-shrink-0 p-4 border-b border-neutral-border bg-[#fafafa] rounded-t-2xl">
                <h2 id="demo-modal-title" className="text-lg font-semibold text-neutral-text flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-primary" />
                  Возможности AI Tutor
                </h2>
                <button
                  type="button"
                  onClick={() => setIsDemoModalOpen(false)}
                  className="p-2 rounded-lg hover:bg-neutral-border/40 transition-colors text-neutral-text hover:text-primary"
                  title="Закрыть"
                  aria-label="Закрыть"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* FAQ */}
                <section aria-labelledby="demo-faq-heading">
                  <h3 id="demo-faq-heading" className="flex items-center gap-2 text-base font-semibold text-neutral-text mb-3">
                    <HelpCircle className="w-4 h-4 text-primary" />
                    Частые вопросы
                  </h3>
                  <ul className="space-y-3">
                    {DEMO_FAQ.map((faq, i) => (
                      <li key={i} className="rounded-xl border border-neutral-border/60 bg-neutral-light/30 p-3">
                        <p className="text-sm font-medium text-neutral-text mb-1">{faq.q}</p>
                        <p className="text-sm text-neutral-text/80 leading-relaxed">{faq.a}</p>
                      </li>
                    ))}
                  </ul>
                </section>

                {/* Примеры по категориям */}
                <section aria-labelledby="demo-capabilities-heading">
                  <h3 id="demo-capabilities-heading" className="text-base font-semibold text-neutral-text mb-3">
                    Примеры того, что можно спросить
                  </h3>
                  <ul className="space-y-4">
                    {DEMO_CAPABILITIES.map((cap) => {
                      const Icon = cap.icon;
                      return (
                        <li key={cap.id} className="rounded-xl border border-neutral-border/60 bg-white p-4 shadow-sm">
                          <div className="flex gap-3">
                            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                              <Icon className="w-4 h-4 text-primary" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h4 className="text-sm font-semibold text-neutral-text mb-1">{cap.title}</h4>
                              <p className="text-sm text-neutral-text/80 leading-relaxed mb-3">{cap.description}</p>
                              <ul className="space-y-1">
                                {cap.examples.map((ex, j) => (
                                  <li key={j} className="text-xs text-neutral-text/70 pl-2 border-l-2 border-primary/30">
                                    «{ex}»
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              </div>

              <div className="flex-shrink-0 p-4 border-t border-neutral-border bg-[#fafafa] rounded-b-2xl">
                <button
                  type="button"
                  onClick={() => setIsDemoModalOpen(false)}
                  className="w-full py-2.5 px-4 rounded-xl text-sm font-medium bg-primary text-white hover:bg-primary/90 transition-colors"
                >
                  Попробовать в чате
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
